import React, {
  useState,
  createContext,
  useMemo,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { verifyToken, getBearerToken } from '../Pages/LoginPage/userData';
import AxiosWrapper from '../Api/http/AxiosWrapper';

const AuthContext = createContext(null);
const Wrapper = new AxiosWrapper();

export const useAuth = () => useContext(AuthContext);

export const checkCredentials = async () => {
  const authToken = Wrapper.getAuthToken();
  if (!authToken) return null;

  const userDetail = await verifyToken(authToken);
  if (!userDetail || Object.keys(userDetail).length === 0) return null;

  const userRoles = await getBearerToken(userDetail.email);
  const mappedPermissions = userRoles.permissions.reduce((acc, { module, actions }) => {
    acc[module] = actions;
    return acc;
  }, {});
  userDetail.permissions = mappedPermissions;
  userDetail.roleDetails = userRoles.roleDetails;
  userDetail.userPortalId = userRoles.id;

  return userDetail;
};

const handleLoginResult = (setLoggedIn) => (result) => {
  if (result) setLoggedIn(true);
};

const handleLoginError = (error) => {
  toast.error('Authentication failed');
  Wrapper.removeAuthToken();
  clearLocalDataOnLogout();
  throw error;
};

const handleLogoutResult = (setLoggedIn) => (result) => {
  if (result) setLoggedIn(false);
};

const handleLogoutError = (error) => {
  toast.error('Logout failed');
  throw error;
};

const clearLocalDataOnLogout = () => {
  localStorage.removeItem('activeMenuItem'); // Clear the saved activeMenuItem from localStorage
  localStorage.removeItem('activeSubmenuItem'); // Clear the saved activeSubmenuItem from localStorage
  localStorage.removeItem('activeSubmenuOption'); // Clear the saved activeSubmenuOption from localStorage
  localStorage.removeItem('userRolesToken'); // Clear the saved userRolesToken from localStorage
  localStorage.removeItem('userPermissions');
};

const AuthProvider = ({ children }) => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [userProfile, setUserProfile] = useState([]);

  const login = useCallback(async () => {
    try {
      const result = await checkCredentials();
      if (result) {
        setUserProfile(result);
        handleLoginResult(setLoggedIn)(result);
        return result;
      } else {
        await logout();
        return null;
      }
    } catch (error) {
      handleLoginError(error);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      if (localStorage.getItem('authToken')) {
        localStorage.setItem('intentionalLogout', 'true');
      }
      Wrapper.deauthenticate(); // Function to remove auth token from local storage
      clearLocalDataOnLogout();
      handleLogoutResult(setLoggedIn)(
        'The user has successfully logged on the server',
      );
    } catch (error) {
      handleLogoutError(error);
    }
  }, []);

  useEffect(() => {
    login();
  }, [login]);

  const authContextValue = useMemo(
    () => ({
      loggedIn,
      setLoggedIn,
      login,
      logout,
      userProfile,
      setUserProfile,
    }),
    [loggedIn, login, logout, userProfile],
  );

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { AuthContext, AuthProvider };
